import { Checkbox } from '@recruit-robin/robin-theme/web-components';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useGetJobImproved } from '@/entities/job';
import { useCurrentUserQuery, useUserFeatureToggle, userFeatureToggles } from '@/entities/user';
import { useBulkGenerateReachoutMessages } from '@/features/generate-ai-reachout-message';
import { useRjWhatsappMessageBuilder, buildRjWhatsappJobDetails } from '@/shared/lib/rj-whatsapp-message';
import type { JobSummaryViewModel } from '@/features/job-improved-selection';
import { useHasCalendarConfiguration } from '@/features/nylas-calendar-manage-connection-modal';
import { ReviewQuestionnaireDialog } from '@/features/review-questionnaire/ui';
import type { CandidateRequirementsDTO } from '@/entities/questionnaire';
import { IndeedProjectCreditsSection, useIndeedBulkJobSelection } from '@/features/indeed-job-selection-bulk';
import { useDictionary } from '@/shared/lib/i18n';

import { useBulkSendPipeline, useBulkIndeedSendPipeline, type BulkSendItem } from '../../api';
import type { IndeedBulkSendItem } from '../../api';
import { BulkGenerationProgress, type BulkGenerationProgressRef } from '../bulk-generation-progress';
import { BulkSendingProgress } from '../bulk-sending-progress';
import { SelectJobModal } from '../select-job-modal';
import type { RjCommBulkResult, RjCommBulkWidgetProps } from './RjCommBulkWidget.types';
import { AutoSendInterviewInvite, useNylasCalendarStore } from '@/features/nylas-calendar-manage-connection-modal';

type Step = 1 | 2 | 3 | 4;

export function RjCommBulkWidget({
  isVisible,
  campaignId,
  onClose,
  candidates,
  initialJob,
  onComplete,
  onRemoveCandidate,
  onStartCandidate,
  onEditJob,
}: RjCommBulkWidgetProps) {
  const [step, setStep] = useState<Step>(1);
  const [selectedJob, setSelectedJob] = useState<JobSummaryViewModel | null>(initialJob ?? null);

  const queryClient = useQueryClient();
  const bulk = useBulkGenerateReachoutMessages(5);
  const sendPipeline = useBulkSendPipeline(3);
  const indeedSendPipeline = useBulkIndeedSendPipeline(3);
  const indeedSelection = useIndeedBulkJobSelection();
  const dictionary = useDictionary();
  const { data: currentUser } = useCurrentUserQuery();
  const buildWhatsappMessage = useRjWhatsappMessageBuilder();
  const progressRef = useRef<BulkGenerationProgressRef>(null);
  const bulkSendResultRef = useRef<RjCommBulkResult | null>(null);
  const lastConfirmedJobDbIdRef = useRef<number | undefined>(undefined);
  const hasCalendarConfiguration = useHasCalendarConfiguration();

  const emailCandidates = useMemo(
    () => candidates.filter((c) => c.commAgentFlow === 'email' || c.commAgentFlow === 'whatsapp'),
    [candidates],
  );
  const indeedCandidates = useMemo(() => candidates.filter((c) => c.commAgentFlow === 'indeed'), [candidates]);
  const hasIndeedCandidates = indeedCandidates.length > 0;

  const {
    checked: dontShowQualificationQuestionaryPreview,
    checkAndPersist: checkAndPersistQualificationQuestionaryPreviewToggle,
  } = useUserFeatureToggle(userFeatureToggles.dontShowQualificationQuestionaryPreview);

  const { checked: isAutoSendInterviewInviteEnabled, checkAndPersist: checkAndPersistAutoSendInterviewInviteToggle } =
    useUserFeatureToggle(userFeatureToggles.autoSendInterviewInvite, hasCalendarConfiguration);

  useEffect(() => {
    if (isVisible) {
      setStep(1);
      setSelectedJob(initialJob ?? null);
      bulkSendResultRef.current = null;
      lastConfirmedJobDbIdRef.current = initialJob?.dbId ?? undefined;
      bulk.reset();
      sendPipeline.reset();
      indeedSendPipeline.reset();
    }
  }, [isVisible]);

  const jobId = selectedJob?.dbId ?? undefined;
  const hasValidJobId = jobId != null && typeof jobId === 'number';

  const handleSelectJobConfirm = (job: JobSummaryViewModel) => {
    const nextDbId = job.dbId ?? undefined;
    if (nextDbId !== lastConfirmedJobDbIdRef.current) {
      bulk.reset();
    }
    lastConfirmedJobDbIdRef.current = nextDbId;
    setSelectedJob(job);
    dontShowQualificationQuestionaryPreview ? handleQuestionnaireConfirm(job.dbId ?? undefined) : setStep(2);
  };

  const handleQuestionnaireConfirm = useCallback(
    async (confirmedJobId: number | undefined) => {
      setStep(3);

      const currentJobId = confirmedJobId ?? selectedJob?.dbId;
      if (currentJobId == null) return;

      const fetchedImprovedJob = await useGetJobImproved.fetchOrCache(queryClient, currentJobId);

      const companyName = currentUser?.tenant?.name ?? '';
      const recruiterFirstName = currentUser?.first_name ?? '';
      const vacancyParams = buildRjWhatsappJobDetails(fetchedImprovedJob, selectedJob);

      bulk.generate(
        candidates.map((c) => {
          const isWhatsapp = c.commAgentFlow === 'whatsapp';
          const outreachType =
            c.commAgentFlow === 'whatsapp' ? 'whatsapp' : c.commAgentFlow === 'indeed' ? 'indeed' : 'email';

          return {
            es_person_id: c.esPersonId,
            job_improved_id: currentJobId,
            outreach_type: outreachType,
            language: 'auto' as const,
            candidateName: c.name ?? '',
            whatsappTemplateMessage: isWhatsapp
              ? buildWhatsappMessage({
                  candidateFirstName: c.firstName ?? c.name?.split(' ')[0] ?? '',
                  recruiterFirstName,
                  companyName,
                  jobBoardName: c.jobBoardName ?? '',
                  includeEmailNote: c.hasEmail,
                  ...vacancyParams,
                })
              : undefined,
          };
        }),
      );
    },
    [selectedJob, candidates, bulk, queryClient, currentUser, buildWhatsappMessage],
  );

  const handleRegenerate = useCallback(
    async (esPersonId: string) => {
      const candidate = candidates.find((c) => c.esPersonId === esPersonId);
      if (candidate?.commAgentFlow === 'whatsapp') return;

      const currentJobId = selectedJob?.dbId;
      if (currentJobId == null) return;

      const outreachType = candidate?.commAgentFlow === 'indeed' ? 'indeed' : 'email';
      await bulk.regenerateSlot(esPersonId, {
        job_improved_id: currentJobId,
        outreach_type: outreachType,
        language: 'auto' as const,
      });
    },
    [selectedJob, candidates, bulk],
  );

  const handleDeleteCandidate = useCallback(
    (esPersonId: string) => {
      bulk.removeSlot(esPersonId);
      onRemoveCandidate?.(esPersonId);
    },
    [bulk.removeSlot, onRemoveCandidate],
  );

  const handleSendSuccess = useCallback(
    (esPersonId: string) => {
      onStartCandidate?.(esPersonId);
      bulk.removeSlot(esPersonId);
      onRemoveCandidate?.(esPersonId);
    },
    [onStartCandidate, bulk.removeSlot, onRemoveCandidate],
  );

  const handleBulkComplete = useCallback(() => {
    if (jobId == null) return;

    const candidateResults = progressRef.current?.getResults() ?? [];
    if (candidateResults.length === 0) return;

    bulkSendResultRef.current = {
      jobId: jobId,
      questionnaireState: {} as CandidateRequirementsDTO,
      candidates: candidateResults,
    };

    const emailCandidateIds = new Set(emailCandidates.map((c) => c.esPersonId));
    const indeedCandidateIds = new Set(indeedCandidates.map((c) => c.esPersonId));

    const emailItems: BulkSendItem[] = candidateResults
      .filter((r) => emailCandidateIds.has(r.esPersonId))
      .map((r) => {
        const candidate = emailCandidates.find((c) => c.esPersonId === r.esPersonId);
        const isWhatsapp = candidate?.commAgentFlow === 'whatsapp';

        return {
          esPersonId: r.esPersonId,
          candidateName: bulk.slots.get(r.esPersonId)?.candidateName ?? '',
          campaignId,
          improvedJobId: jobId,
          subject: isWhatsapp ? undefined : r.subject,
          message: r.message,
          schedulerType: hasCalendarConfiguration ? useNylasCalendarStore.getState().interviewType : undefined,
          isAutoSendInterviewInviteEnabled: hasCalendarConfiguration ? isAutoSendInterviewInviteEnabled : undefined,
          flow: 'email' as const,
        };
      });

    if (emailItems.length > 0) {
      sendPipeline.send(emailItems, handleSendSuccess);
    }

    if (hasIndeedCandidates && indeedSelection.selectedProject) {
      const indeedJob =
        selectedJob?.type === 'INDEED' && selectedJob?.jobIndeedId
          ? { key: selectedJob.jobIndeedId, name: selectedJob.jobTitle ?? '' }
          : null;

      const indeedItems: IndeedBulkSendItem[] = candidateResults
        .filter((r) => indeedCandidateIds.has(r.esPersonId))
        .map((r) => {
          const candidate = indeedCandidates.find((c) => c.esPersonId === r.esPersonId);
          return {
            esPersonId: r.esPersonId,
            candidateName: bulk.slots.get(r.esPersonId)?.candidateName ?? '',
            campaignId,
            candidateStatus: candidate?.candidateStatus as IndeedBulkSendItem['candidateStatus'],
            subject: r.subject,
            message: r.message,
            improvedJobId: jobId,
            isAutoSendInterviewInviteEnabled: hasCalendarConfiguration ? isAutoSendInterviewInviteEnabled : undefined,
          };
        });

      if (indeedItems.length > 0) {
        indeedSendPipeline.send(
          indeedItems,
          { indeedProject: indeedSelection.selectedProject, indeedJob },
          handleSendSuccess,
        );
      }
    }

    setStep(4);
  }, [
    jobId,
    campaignId,
    bulk.slots,
    emailCandidates,
    indeedCandidates,
    hasIndeedCandidates,
    indeedSelection.selectedProject,
    selectedJob,
    sendPipeline,
    indeedSendPipeline,
    handleSendSuccess,
    hasCalendarConfiguration,
    isAutoSendInterviewInviteEnabled,
  ]);

  const emailIsComplete = emailCandidates.length === 0 || sendPipeline.isComplete;
  const indeedIsComplete = !hasIndeedCandidates || indeedSendPipeline.isComplete;
  const bothPipelinesComplete = emailIsComplete && indeedIsComplete;

  useEffect(() => {
    if (step !== 4 || !bothPipelinesComplete) return;

    const result = bulkSendResultRef.current;
    if (result) {
      onComplete?.(result);
    }
  }, [step, bothPipelinesComplete, onComplete]);

  const combinedSlots = useMemo(() => {
    if (step !== 4) return sendPipeline.slots;
    return new Map([...sendPipeline.slots, ...indeedSendPipeline.slots]);
  }, [step, sendPipeline.slots, indeedSendPipeline.slots]);

  const footer = useMemo(
    () =>
      dontShowQualificationQuestionaryPreview && (
        <Checkbox
          checked={dontShowQualificationQuestionaryPreview}
          onClick={() => checkAndPersistQualificationQuestionaryPreviewToggle(!dontShowQualificationQuestionaryPreview)}
          label={dictionary.dontShowQualificationQuestionaryPreviewNextTime}
        />
      ),
    [dontShowQualificationQuestionaryPreview, dictionary],
  );

  const handleBack = useCallback(() => {
    setStep((prev) => (prev === 1 ? 1 : dontShowQualificationQuestionaryPreview ? 1 : ((prev - 1) as Step)));
  }, [step, dontShowQualificationQuestionaryPreview]);

  const handleSendingClose = useCallback(() => {
    sendPipeline.reset();
    indeedSendPipeline.reset();
    onClose();
  }, [sendPipeline, indeedSendPipeline, onClose]);

  const handleRetrySend = useCallback(
    (esPersonId: string) => {
      if (indeedSendPipeline.slots.has(esPersonId)) {
        indeedSendPipeline.retrySend(esPersonId);
      } else {
        sendPipeline.retrySend(esPersonId);
      }
    },
    [sendPipeline, indeedSendPipeline],
  );

  if (!isVisible) return null;

  if (step === 1) {
    return (
      <SelectJobModal
        isVisible={true}
        onConfirm={handleSelectJobConfirm}
        onCancel={onClose}
        onSelectJob={setSelectedJob}
        footer={footer}
        initialJob={selectedJob}
        {...(onEditJob ? { onEditJob } : {})}
        slot={hasIndeedCandidates ? <IndeedProjectCreditsSection selection={indeedSelection} /> : undefined}
        isConfirmDisabled={hasIndeedCandidates && !indeedSelection.isConfirmReady}
      />
    );
  }

  if (step === 2 && hasValidJobId && !dontShowQualificationQuestionaryPreview) {
    return (
      <ReviewQuestionnaireDialog
        isVisible={true}
        onClose={onClose}
        jobId={jobId}
        onConfirm={() => handleQuestionnaireConfirm(jobId)}
        onGoBack={() => setStep(1)}
        slot={
          hasCalendarConfiguration && (
            <AutoSendInterviewInvite
              isAutoSendInterviewInviteEnabled={isAutoSendInterviewInviteEnabled}
              onToggle={() => checkAndPersistAutoSendInterviewInviteToggle(!isAutoSendInterviewInviteEnabled)}
            />
          )
        }
      />
    );
  }

  if (step === 3 && hasValidJobId) {
    return (
      <BulkGenerationProgress
        ref={progressRef}
        isVisible={true}
        jobImprovedId={jobId}
        slots={bulk.slots}
        isComplete={bulk.isComplete}
        candidates={candidates}
        onComplete={handleBulkComplete}
        onBack={handleBack}
        onDelete={handleDeleteCandidate}
        onRegenerate={handleRegenerate}
        onClose={onClose}
      />
    );
  }

  if (step === 4) {
    return <BulkSendingProgress slots={combinedSlots} onClose={handleSendingClose} onRetry={handleRetrySend} />;
  }

  return null;
}
